import { EditorState, convertToRaw, convertFromRaw } from "draft-js";

// Internal Imports
import { DOCUMENTS, DRAFT } from "../constants";
import { DOCUMENT_BLOCKS } from "../components/DocumentV2/constants";

// Internal Imports
import { normaliseTableData } from "./tableUtils";
import HTMLtoDraft from "./HTMLtoDraft/HTMLtoDraft";

// Destructuring
const { COMPONENT_FEATURE, DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;

export const getComponentData = (components, componentMeta) => {
  if (!(componentMeta || components)) return null;

  const { componentId, blockType } = componentMeta;
  let component;
  if (blockType === DOCUMENT_BLOCKS.BODY) {
    component = components[componentId];
  } else if (
    blockType === DOCUMENT_BLOCKS.COVERPAGE &&
    componentMeta.coverpageId
  ) {
    const { coverpageId } = componentMeta;
    component =
      components[coverpageId] &&
      components[coverpageId].content &&
      components[coverpageId].content.data
        ? components[coverpageId].content.data.components[componentId]
        : null;
  } else if (blockType === DOCUMENT_BLOCKS.HEADER && componentMeta.headerId) {
    const { headerId } = componentMeta;
    component =
      components[headerId] &&
      components[headerId].content &&
      components[headerId].content.data &&
      components[headerId].content.data.header
        ? components[headerId].content.data.header.components[componentId]
        : null;
  } else if (blockType === DOCUMENT_BLOCKS.FOOTER && componentMeta.footerId) {
    const { footerId } = componentMeta;
    component =
      components[footerId] &&
      components[footerId].content &&
      components[footerId].content.data &&
      components[footerId].content.data.footer
        ? components[footerId].content.data.footer.components[componentId]
        : null;
  }

  if (!component) return null;

  return component;
};

export const getComponentEditorData = (components, componentMeta) => {
  // const component = getComponentFromInfo(components, componentMeta);
  const component = getComponentData(components, componentMeta);
  if (!component) return null;

  const { content } = component;
  const { componentType } = componentMeta;
  if (componentType === DOCUMENT_COMPONENT_TYPES.HTML) {
    return content;
  }
  if (componentType === DOCUMENT_COMPONENT_TYPES.TABLE) {
    const { data: tableData } = content;
    const { tableRowId, tableColId } = componentMeta;
    if (
      tableData &&
      tableData.data[tableRowId] &&
      tableData.data[tableRowId].data[tableColId]
    ) {
      return tableData.data[tableRowId].data[tableColId].data;
    }
    return null;
  }
  return null;
};

export const convertRawToDraft = data => {
  try {
    if (
      data &&
      typeof data === "object" &&
      DRAFT.DRAFT_RAW_KEYS.length === Object.keys(data).length &&
      JSON.stringify(DRAFT.DRAFT_RAW_KEYS.sort()) ===
        JSON.stringify(Object.keys(data).sort())
    ) {
      return convertFromRaw(data);
    }
    console.error(`Raw to draft conversion error. Raw keys unavailable`);
  } catch (error) {
    throw Error(
      `Unable to convert raw state to draft state. Error: ${error.message}`,
    );
  }
  const editorState = EditorState.createEmpty();
  return editorState.getCurrentContent();
};

export const convertHTMLStringToDraft = (html, mode) => {
  try {
    return HTMLtoDraft(html, mode);
  } catch (error) {
    console.error(
      `Unable to convert the html object into editor object. Error: ${
        error.message
      }`,
    );
    throw Error(
      `Unable to convert the html object into editor object. Error: ${
        error.message
      }`,
    );
  }
};

export const convertHTMLRawToDraft = (section, mode) => {
  const draftData = {};

  if (typeof section.data === "string") {
    const contentState = convertHTMLStringToDraft(section.data, mode);
    draftData.data = contentState;
    // if it is for document, diff state should be initialized
    if (COMPONENT_FEATURE[mode].allowDiff) {
      draftData.currentContent = contentState;
    }
  } else if (typeof section.data === "object") {
    draftData.data = convertRawToDraft(section.data);
    if (COMPONENT_FEATURE[mode].allowDiff) {
      // check current content
      draftData.currentContent = section.currentContent
        ? convertRawToDraft(section.currentContent)
        : draftData.data;
    }
  } else {
    draftData.data = convertRawToDraft(section.data);
    if (COMPONENT_FEATURE[mode].allowDiff) {
      // check current content
      draftData.currentContent = section.currentContent
        ? convertRawToDraft(section.currentContent)
        : draftData.data;
    }
  }
  return draftData;
};

export const convertTableRawtoDraft = (section, mode) => {
  let tableData = normaliseTableData(section.data);
  if (Array.isArray(tableData.data)) {
    const draftTableData = tableData.data.map(row => ({
      ...row,
      data: row.data.map(cell => ({
        ...cell,
        data: convertHTMLRawToDraft(cell.data, mode),
      })),
    }));
    tableData = {
      ...tableData,
      data: draftTableData,
    };
  }
  return tableData;
};

export const convertSectionRawToDraft = (section, mode) => {
  let modifiedSection = {
    id: section.section_id || section.id,
    type: section.section_type || section.type,
    lock: section.lock || false,
    label: section.section_name || section.label,
    header: section.header,
    data: section.data,
    clauseID: section.clauseID || section.clause,
    clauseOptions: section.clauseOptions || null,
  };
  if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.HTML) {
    modifiedSection = {
      ...modifiedSection,
      ...convertHTMLRawToDraft(section, mode),
    };
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.DEAL_TABLE) {
    modifiedSection.showGroupedData = section.showGroupedData;
    modifiedSection.showUngroupedData = section.showUngroupedData;
    modifiedSection.dealtableView = section.dealtableView;
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
    modifiedSection.data = convertTableRawtoDraft(section, mode);
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET) {
    modifiedSection.sheet = section.sheet;
    modifiedSection.workSheet = section.workSheet;
    modifiedSection.rangeValue = section.rangeValue;
    modifiedSection.autoTrim = section.autoTrim;
    modifiedSection.connected = section.connected;
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
    const headerComponents = {};
    const footerComponents = {};
    Object.keys(section.data.footer.components).forEach(componentId => {
      footerComponents[componentId] = {
        ...section.data.footer.components[componentId],
        content: convertSectionRawToDraft(
          section.data.footer.components[componentId].content,
          mode,
        ),
      };
    });
    Object.keys(section.data.header.components).forEach(componentId => {
      headerComponents[componentId] = {
        ...section.data.header.components[componentId],
        content: convertSectionRawToDraft(
          section.data.header.components[componentId].content,
          mode,
        ),
      };
    });
    modifiedSection.data = {
      footer: {
        ...section.data.footer,
        components: footerComponents,
      },
      header: {
        ...section.data.header,
        components: headerComponents,
      },
    };
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
    const newComponents = {};
    Object.keys(section.data.components).forEach(componentId => {
      newComponents[componentId] = {
        ...section.data.components[componentId],
        content: convertSectionRawToDraft(
          section.data.components[componentId].content,
          mode,
        ),
      };
    });
    modifiedSection.data = {
      ...section.data,
      components: newComponents,
    };
  }
  return modifiedSection;
};

export const getComponentMetaFromEntity = (components, entity) => {
  const { sectionID, id: entityID } = entity;

  let section;
  const componentMeta = {
    componentId: sectionID,
  };
  if (components[sectionID] === undefined) {
    // check if it is in coverpage or header footer
    const coverpageId = Object.keys(components).find(
      componentId =>
        components[componentId].content.type ===
        DOCUMENT_COMPONENT_TYPES.COVERPAGE,
    );

    const headerFooterId = Object.keys(components).find(
      componentId =>
        components[componentId].content.type ===
        DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER,
    );

    if (
      coverpageId &&
      components[coverpageId].content.data.components[sectionID] !== undefined
    ) {
      componentMeta.blockType = DOCUMENT_BLOCKS.COVERPAGE;
      componentMeta.coverpageId = coverpageId;
      section =
        components[coverpageId].content.data.components[sectionID].content;
    } else if (
      headerFooterId &&
      components[headerFooterId].content.data.header.components[sectionID] !==
        undefined
    ) {
      componentMeta.blockType = DOCUMENT_BLOCKS.HEADER;
      componentMeta.headerId = headerFooterId;
      section =
        components[headerFooterId].content.data.header.components[sectionID]
          .content;
    } else if (
      headerFooterId &&
      components[headerFooterId].content.data.footer.components[sectionID] !==
        undefined
    ) {
      componentMeta.blockType = DOCUMENT_BLOCKS.FOOTER;
      componentMeta.footerId = headerFooterId;
      section =
        components[headerFooterId].content.data.footer.components[sectionID]
          .content;
    }
  } else {
    componentMeta.blockType = DOCUMENT_BLOCKS.BODY;
    section = components[sectionID].content;
  }

  componentMeta.componentType = section.type;

  if (section.type === DOCUMENT_COMPONENT_TYPES.HTML) {
    return componentMeta;
  }
  if (section.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
    const { data: tableData } = section.data;
    let rowID;
    let columnID;
    for (let rowIndex = 0; rowIndex < tableData.length; rowIndex += 1) {
      const row = tableData[rowIndex];
      const { data: rowData } = row;
      for (let colIndex = 0; colIndex < rowData.length; colIndex += 1) {
        const { data: cellData } = rowData[colIndex];
        if (cellData && cellData.data && typeof cellData.data === "object") {
          const rawData = convertToRaw(cellData.data);
          const { entityMap } = rawData;
          const findEntity = Object.keys(entityMap).find(entityKey => {
            const entityData = entityMap[entityKey];
            return entityData.data.id === entityID;
          });
          if (findEntity) {
            rowID = rowIndex;
            columnID = colIndex;
            break;
          }
        }
      }
    }
    componentMeta.tableId = sectionID;
    componentMeta.tableColId = columnID;
    componentMeta.tableRowId = rowID;
  }
  return componentMeta;
};



// WEBPACK FOOTER //
// ./src/utils/components.js